import { useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { ProductCategoryTabs } from '~/components/ProductCategoryTabs/ProductCategoryTabs'
import {
  CatalogFilters,
  INITIAL_FILTERS,
} from '~/components/CatalogFilters/CatalogFilters'
import type { CatalogFilterState } from '~/components/CatalogFilters/CatalogFilters'
import { CatalogProductCard } from '~/components/CatalogProductCard/CatalogProductCard'
import {
  catalogPresets,
  getWidthRange,
  type CatalogProductType,
} from '~/presets/catalogPresets'

import styles from './CatalogSection.module.css'

const DEFAULT_TYPE: CatalogProductType = 'wardrobe'

interface CatalogSectionProps {
  selectedType?: CatalogProductType
  onTypeChange?: (type: CatalogProductType) => void
}

export const CatalogSection: React.FC<CatalogSectionProps> = ({
  selectedType: controlledType,
  onTypeChange,
}) => {
  const [internalType, setInternalType] =
    useState<CatalogProductType>(DEFAULT_TYPE)
  const [filters, setFilters] = useState<CatalogFilterState>(INITIAL_FILTERS)

  const isControlled = controlledType !== undefined
  const selectedType = isControlled ? controlledType : internalType

  const handleTypeChange = (type: CatalogProductType) => {
    if (!isControlled) {
      setInternalType(type)
    }
    setFilters(INITIAL_FILTERS)
    onTypeChange?.(type)
  }

  const filteredPresets = useMemo(() => {
    return catalogPresets.filter((preset) => {
      if (selectedType && preset.type !== selectedType) return false

      if (filters.widthRanges.length > 0) {
        const range = getWidthRange(preset.dimensions.width)
        if (!filters.widthRanges.includes(range)) return false
      }

      if (filters.collections.length > 0) {
        if (!filters.collections.includes(preset.collection)) return false
      }

      if (filters.structureTypes.length > 0) {
        if (!filters.structureTypes.includes(preset.structureType))
          return false
      }

      return true
    })
  }, [selectedType, filters])

  return (
    <div className={styles.wrapper}>
      <section className={styles.tabsSection}>
        <ProductCategoryTabs
          selected={selectedType}
          onSelect={handleTypeChange}
        />
      </section>

      <section className={styles.filtersSection}>
        <CatalogFilters
          filters={filters}
          onChange={setFilters}
          selectedType={selectedType}
        />
      </section>

      <section className={styles.gridSection}>
        {filteredPresets.length > 0 ? (
          <div className={styles.grid}>
            {filteredPresets.map((preset, index) => (
              <CatalogProductCard key={preset.id} preset={preset} priority={index < 4} />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <FormattedMessage
              id="catalog.empty"
              defaultMessage="Nu au fost gasite produse. Incercati sa modificati filtrele."
            />
          </div>
        )}
      </section>
    </div>
  )
}
